import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useKeycloak } from '@/hooks/use-keycloak-wrapper'
import { useTranslation } from 'react-i18next'
import {
  Settings,
  User,
  LogOut,
  Menu,
  X,
  Crown,
  Bell,
  ChevronRight,
  Search,
} from 'lucide-react'
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SchoolSelector } from '@/components/school/SchoolSelector'
import { useCurrentSchool } from '@/contexts/SchoolContext'
import { LanguageSwitcher } from '@/components/ui/language-switcher'
import { RoleBasedNavigation } from '@/components/navigation/RoleBasedNavigation'
import { getNavigationItem } from '@/components/navigation/RoleBasedNavigation'
import { OnboardingProvider } from '@/components/onboarding/OnboardingProvider'
import { lazy, Suspense } from 'react'
// Markdown rendering is only needed once the visitor opens the support chat.
const SupportChatWidget = lazy(() => import('@/components/support/SupportChatWidget').then(m => ({ default: m.SupportChatWidget })))
import { TrialBanner } from '@/components/subscription/TrialBanner'
import { Breadcrumb, routeSegmentLabel } from '@/components/ui/breadcrumb'
import { FeatureBlockedModal } from '@/components/subscription/FeatureBlockedModal'
import { useAuth } from '@/hooks/use-auth'
import { sendFirstTouch } from '@/lib/attribution'
import { notificationService, type Notification as AppNotification } from '@/services/notification.service'
import { CommandPalette } from '@/components/admin/CommandPalette'
import { EnrolStudentWizard } from '@/components/admin/EnrolStudentWizard'

export function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { keycloak } = useKeycloak()
  const { t } = useTranslation()
  const { currentSchool, isLoadingSchool, schoolError } = useCurrentSchool()
  const { user, logout } = useAuth()

  // a new account tells us once which campaign link brought it
  useEffect(() => {
    if (user) sendFirstTouch()
  }, [user])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const notifRef = useRef<HTMLDivElement>(null)
  const [blockedFeature, setBlockedFeature] = useState<{ feature: string; message: string } | null>(null)

  // Listen for subscription limit events from API interceptor
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setBlockedFeature({ feature: detail.feature, message: detail.message })
    }
    window.addEventListener('subscription:limit-reached', handler)
    return () => window.removeEventListener('subscription:limit-reached', handler)
  }, [])

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationService.getNotifications({ size: 10 })
      setNotifications(res.items)
      setUnreadCount(res.unread_count)
    } catch {
      // silently fail - user may not be authenticated yet
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
    }
    if (notifOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [notifOpen])

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead()
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch { /* ignore */ }
  }

  const handleMarkRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch { /* ignore */ }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    logout()
  }

  const openCommandPalette = () => {
    window.dispatchEvent(new Event('admin:open-command-palette'))
  }

  // Derive breadcrumb from current route
  const breadcrumb = useMemo(() => {
    const navItem = getNavigationItem(location.pathname)
    if (navItem) {
      return routeSegmentLabel(navItem.name, t)
    }
    const segments = location.pathname.split('/').filter(Boolean)
    return routeSegmentLabel(segments[segments.length - 1] || 'dashboard', t)
  }, [location.pathname, t])

  const userInitials = useMemo(() => {
    const name = user?.full_name || user?.name || keycloak.tokenParsed?.preferred_username || '?'
    const parts = name.split(' ')
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase()
  }, [user, keycloak.tokenParsed])

  const displayName = user?.full_name || user?.name || keycloak.tokenParsed?.preferred_username || ''
  const userRole = user?.primary_role || (Array.isArray(user?.roles) ? user.roles[0] : undefined) || 'student'

  return (
    <OnboardingProvider>
      <div className="flex h-screen overflow-hidden bg-[#F6F8FB] dark:bg-gray-950">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 transform border-r border-[#E7ECF2] dark:border-gray-800/60 bg-white dark:bg-gray-900 transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex h-16 items-center justify-between px-4" data-tour="dashboard-header">
              <Link to="/app/dashboard" className="flex items-center gap-2.5">
                <img src="/logo-icon.svg" alt="AppAcademia" className="h-9 w-9" />
                <span className="font-display text-lg font-bold bg-gradient-to-r from-[#0B4DBA] to-[#FF7A00] bg-clip-text text-transparent">
                  {t('common.appName')}
                </span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-label={t('common.closeSidebar', 'Close sidebar')}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* School Selector (nothing to pick for accounts without a school) */}
            {(currentSchool || isLoadingSchool || schoolError) && (
              <div className="mx-3 mb-3 rounded-xl bg-slate-50/80 dark:bg-gray-800/30 p-2 border border-slate-100 dark:border-gray-800">
                <SchoolSelector variant="compact" />
              </div>
            )}

            {/* Navigation */}
            <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <RoleBasedNavigation onItemClick={() => setSidebarOpen(false)} />
            </div>

            {/* Subscription Badge — only for individual users or school admins, not school staff */}
            {user?.is_freemium_user && (!user.school_id || user.primary_role === 'school_admin' || user.primary_role === 'admin') && (
              <div className="px-3 pb-3">
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/40 dark:to-secondary-900/40 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="h-4 w-4 text-primary-600" />
                    <span className="text-sm font-medium text-primary-800 dark:text-primary-300">
                      {user.subscription_tier === 'free' ? t('subscription.freePlan', 'Free Plan') : `${user.subscription_tier} Plan`}
                    </span>
                    {user.trial_mode && (
                      <Badge variant="secondary" className="text-xs">
                        {t('subscription.trial', 'Trial')}
                      </Badge>
                    )}
                  </div>
                  <Link
                    to="/app/pricing"
                    className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                  >
                    {t('subscription.upgrade', 'Upgrade for unlimited access →')}
                  </Link>
                </div>
              </div>
            )}

            {/* User section */}
            <div className="border-t border-[#E7ECF2] dark:border-gray-800/60 p-3">
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-gray-800/40 px-3 py-2.5">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#13225B] text-xs font-semibold text-white shadow-sm">
                  {userInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#13225B] dark:text-gray-100 truncate">{displayName}</p>
                  <p className="text-[11px] font-medium text-slate-400 dark:text-gray-500">{t(`common.roleNames.${userRole}`, userRole.replace(/_/g, ' '))}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  <Link
                    to="/app/settings"
                    className="rounded-md p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-700/40 transition-colors duration-200"
                    title={t('common.settings', 'Settings')}
                  >
                    <Settings className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="rounded-md p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors duration-200"
                    title={t('common.logout', 'Sign out')}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Top bar */}
          <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-white/85 dark:bg-gray-900/80 backdrop-blur-xl border-b border-[#E7ECF2] dark:border-gray-800/60 px-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label={t('common.openSidebar', 'Open sidebar')}
              >
                <Menu className="h-5 w-5" />
              </Button>
              {/* Breadcrumb */}
              <nav className="hidden sm:flex items-center gap-1.5 text-sm">
                <Link to="/app/dashboard" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  {t('common.appName')}
                </Link>
                <ChevronRight className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600" />
                <span className="font-medium text-gray-700 dark:text-gray-200">{breadcrumb}</span>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openCommandPalette}
                className="hidden h-9 items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-500 shadow-sm transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 md:flex"
              >
                <Search className="h-4 w-4" />
                <span>{t('common.search', 'Search')}</span>
                <kbd className="ml-2 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-400 shadow-sm">
                  Cmd K
                </kbd>
              </button>
              <LanguageSwitcher />
              <div ref={notifRef} className="relative">
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="relative rounded-lg p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  aria-label={t('notifications.title', 'Notifications')}
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#FF7A00] text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-900">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-96 rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 z-50 overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{t('notifications.title', 'Notifications')}</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                        >
                          {t('notifications.markAllRead', 'Mark all read')}
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-400">
                          <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t('notifications.empty', 'No notifications yet')}</p>
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => !n.is_read && handleMarkRead(n.id)}
                            className={cn(
                              'w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 dark:border-gray-700 transition-colors',
                              !n.is_read && 'bg-primary-50/50 dark:bg-primary-900/10'
                            )}
                          >
                            <div className="flex items-start gap-3">
                              {!n.is_read && (
                                <div className="mt-1.5 h-2 w-2 rounded-full bg-primary-500 shrink-0" />
                              )}
                              <div className={cn('flex-1 min-w-0', n.is_read && 'ml-5')}>
                                <p className={cn('text-sm', !n.is_read ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300')}>
                                  {n.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{notificationService.formatTimeAgo(n.created_at)}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Link
                to="/app/profile"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[#13225B] text-xs font-semibold text-white shadow-sm transition-shadow duration-200 hover:shadow-md"
              >
                {userInitials}
              </Link>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-y-auto bg-[#F6F8FB] p-4 dark:bg-gray-950 lg:p-6">
            <TrialBanner />
            <Breadcrumb />
            <div className="mx-auto max-w-[1180px]">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      {/* Support chatbot — available on all pages */}
      <Suspense fallback={null}><SupportChatWidget /></Suspense>
      {/* Subscription limit modal — triggered by API 402 responses */}
      <CommandPalette />
      <EnrolStudentWizard />
      {blockedFeature && (
        <FeatureBlockedModal
          isOpen={!!blockedFeature}
          onClose={() => setBlockedFeature(null)}
          featureName={blockedFeature.feature}
          onUpgrade={() => {
            setBlockedFeature(null)
            navigate('/app/pricing')
          }}
        />
      )}
    </OnboardingProvider>
  )
}
